import { useCallback, useMemo, useState } from "react";
import useStatusMutation from "./useStatusMutation";
import { postRegisterChallenge, putRegisterCredential } from "api/authAPI";
import {
  ChallengeResponse,
  GetLoginResponse,
} from "api/interfaces/authInterface.interface";
import {
  CommonResponse,
  CustomError,
} from "api/interfaces/commonInterface.interface";
import {
  createCredential,
  getPublicKeyCredentialCreationOptions,
} from "utils/AuthUtil";
import { createUuid, getBrowserInfo, getClientDevice } from "utils/functions";
import useApiError from "./useApiError";

export default function useRegisterPasskey({
  onSuccess,
  onFail,
}: {
  onSuccess: () => void;
  onFail: () => void;
}) {
  const [loginData, setLoginData] = useState<GetLoginResponse | null>(null);
  const {
    handleErrorNotify,
    createCustomError,
    notifySuccess,
    intlString,
    lang,
  } = useApiError();
  const errorMsg = intlString(lang.PasskeyErrorCode["110005"]);
  const onFinish = useCallback(
    (isSuccess: boolean = false) => {
      if (isSuccess) {
        onSuccess();
      } else {
        onFail();
      }
    },
    [loginData, onSuccess]
  );
  const mutationRegisterCredential = useStatusMutation(putRegisterCredential, {
    onSuccess: (data: CommonResponse) => {
      if (data.error !== 0) {
        handleErrorNotify(createCustomError(data, errorMsg), errorMsg);
      } else {
        notifySuccess(lang.PasskeyErrorCode["110006"]);
      }
      onFinish(true);
    },
    onError: (e: CustomError) => {
      console.log(e);
      handleErrorNotify(e, errorMsg);
      onFinish();
    },
  });
  const mutationRegisterChallenge = useStatusMutation(postRegisterChallenge, {
    onSuccess: async (data: ChallengeResponse) => {
      if (data.error !== 0) {
        handleErrorNotify(createCustomError(data, errorMsg), errorMsg);
        onFinish();
        return;
      }
      const {
        challenge,
        rpId,
        rpName,
        timeout,
        deviceName,
        sessionName,
        email,
      } = data.result;
      const publicKeyCredentialRequestOptions: PublicKeyCredentialCreationOptions =
        getPublicKeyCredentialCreationOptions({
          email: email,
          challenge,
          rpName,
          rpId,
          timeout,
        });
      try {
        const { credentialId, publicKey, credential } = await createCredential(
          publicKeyCredentialRequestOptions
        );
        mutationRegisterCredential.mutate({
          email,
          deviceName,
          sessionName,
          credentialId,
          publicKey,
          credential,
        });
      } catch (e: any) {
        if (e.message) {
          const csError = {
            response: {
              data: {
                error: e?.message ?? 110003,
              },
            },
            status: 500,
            headers: "",
          };
          handleErrorNotify(
            csError as unknown as CustomError,
            lang.label.passkey.setupFailed,
            "PasskeyErrorCode"
          );
          onFinish();
          return;
        }
        throw e;
      }
    },
    onError: (e: CustomError) => {
      handleErrorNotify(e as CustomError, lang.label.passkey.setupFailed);
      onFinish();
    },
  });
  const onRegisterPasskey = (email: string) => {
    const deviceName = `${getBrowserInfo()} on ${getClientDevice()}`;
    const sessionName = createUuid(new Date().getTime().toString());

    mutationRegisterChallenge.mutate({
      email,
      deviceName,
      sessionName,
    });
  };
  return {
    onRegisterPasskey,
    setLoginData,
  };
}
